import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Blocks, ShieldCheck, TrendingUp, Wallet, GraduationCap, ChevronRight, Globe, Lock } from 'lucide-react';

const CryptoBlockchain: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Blockchain Institute of Technology";

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900">
            {/* Top Bar */}
            <div className="bg-slate-900 text-slate-400 py-2 px-6 text-xs font-medium flex justify-between items-center">
                <span className="flex items-center gap-2"><Globe size={12} /> Global Leader in Web3 Education</span>
                <div className="flex gap-6">
                    <a href="#" className="hover:text-white transition-colors">Corporate Training</a>
                    <a href="#" className="hover:text-white transition-colors">Research Labs</a>
                    <a href="#" className="hover:text-white transition-colors">Alumni</a>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-md">
                            <Blocks size={20} />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-slate-900">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 font-medium text-sm text-slate-600">
                        <a href="#" className="hover:text-indigo-600 transition-colors">Curriculum</a>
                        <a href="#" className="hover:text-indigo-600 transition-colors">Certifications</a>
                        <a href="#" className="hover:text-indigo-600 transition-colors">Admissions</a>
                        <a href="#" className="hover:text-indigo-600 transition-colors">About Us</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="outline" className="hidden md:flex text-slate-700 border-slate-300 hover:bg-slate-50 font-semibold">
                            Syllabus
                        </Button>
                        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md px-6 shadow-md transition-all">
                            Enroll Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative bg-white py-24 px-6 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:40px_40px] opacity-60"></div>
                
                <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wider mb-6">
                            <ShieldCheck size={14} /> Accredited Blockchain Education
                        </div>
                        <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
                            The Future of <br/>
                            <span className="text-indigo-600">Decentralized Finance.</span>
                        </h1>
                        <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                            Master the technical and economic principles of blockchain technology. From Solidity development to tokenomics, prepare for the web3 economy.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button className="h-14 px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold text-base rounded-md">
                                View Degree Programs
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-base rounded-md gap-2">
                                <GraduationCap size={18} /> Scholarship Info
                            </Button>
                        </div>
                    </div>
                    
                    {/* Hero Graphic - Abstract Blockchain Nodes */}
                    <div className="relative h-[400px] bg-indigo-50 rounded-3xl border border-indigo-100 p-8 flex items-center justify-center overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-white"></div>
                        
                        {/* Central Node */}
                        <div className="relative z-10 w-24 h-24 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-indigo-100 animate-[bounce_3s_infinite]">
                            <Blocks size={40} className="text-indigo-600" />
                        </div>

                        {/* Connected Nodes */}
                        <div className="absolute top-1/4 left-1/4 w-16 h-16 bg-white/80 backdrop-blur rounded-xl shadow-lg flex items-center justify-center border border-indigo-50">
                            <Wallet size={24} className="text-slate-400" />
                        </div>
                        <div className="absolute bottom-1/3 right-1/4 w-16 h-16 bg-white/80 backdrop-blur rounded-xl shadow-lg flex items-center justify-center border border-indigo-50">
                            <Lock size={24} className="text-slate-400" />
                        </div>
                         <div className="absolute top-1/3 right-1/3 w-12 h-12 bg-indigo-100 rounded-full blur-xl opacity-50"></div>
                         
                         {/* Connecting Lines (svg) */}
                         <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
                             <line x1="50%" y1="50%" x2="25%" y2="25%" stroke="#4f46e5" strokeWidth="2" strokeDasharray="5,5" />
                             <line x1="50%" y1="50%" x2="75%" y2="66%" stroke="#4f46e5" strokeWidth="2" strokeDasharray="5,5" />
                         </svg>
                    </div>
                </div>
            </header>

            {/* Features Stacks */}
            <section className="py-24 px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">Core Areas of Study</h2>
                    <p className="text-slate-500 max-w-2xl mx-auto">
                        Our curriculum balances computer science fundamentals with advanced cryptography and economic theory.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Blocks, title: "Protocol Engineering", desc: "Deep dive into Layer 1 and Layer 2 consensus mechanisms." },
                        { icon: Lock, title: "Smart Contract Security", desc: "Auditing, formal verification, and secure coding practices." },
                        { icon: TrendingUp, title: "DeFi Economics", desc: "Automated market makers, lending pools, and governance." },
                    ].map((item, i) => (
                        <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-indigo-200 transition-all hover:-translate-y-1">
                            <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 mb-6">
                                <item.icon size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed mb-6 text-sm">{item.desc}</p>
                            <a href="#" className="inline-flex items-center text-indigo-600 font-bold text-sm hover:underline">
                                Learn More <ChevronRight size={16} className="ml-1" />
                            </a>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-slate-900 text-slate-300 py-16 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1">
                        <div className="flex items-center gap-3 mb-6 text-white">
                            <div className="bg-indigo-600 p-1 rounded">
                                <Blocks size={16} className="text-white" />
                            </div>
                            <span className="font-bold text-lg tracking-tight">{schoolName}</span>
                        </div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Pioneering research and education in distributed ledger technologies since 2018.
                        </p>
                    </div>
                    
                    <div>
                        <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Academics</h4>
                        <ul className="space-y-2 text-sm text-slate-400">
                            <li><a href="#" className="hover:text-white transition-colors">Master of Science</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Bootcamps</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Online Certifications</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Resources</h4>
                        <ul className="space-y-2 text-sm text-slate-400">
                            <li><a href="#" className="hover:text-white transition-colors">Whitepapers</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">FAQs</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Connect</h4>
                        <ul className="space-y-2 text-sm text-slate-400">
                            <li><a href="#" className="hover:text-white transition-colors">Twitter (X)</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Discord Community</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">GitHub</a></li>
                        </ul>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CryptoBlockchain;
